import { useCallback, useMemo } from "react"
import { ScrollView, StyleSheet, Text, View } from "react-native"
import { Button } from "src/components/parts/buttons/Button"
import { Colors } from "src/constants/Colors"
import { SearchInput } from "./SearchInput"
import { CloseIcon } from "./icons/CloseIcon"

type Props<T> = {
  data: { value: T; label: string }[]
  filters: T[]
  searchText?: string
  confirmedSearchText? : string,
  onFiltersChange: (next: T[]) => void
  onSearchTextChange?: (text: string) => void
  onConfirmedSearchTextChange?: (text: string) => void
  isApplicantAuthorityTransfer?: boolean
  isIncrementalTextSearch?: boolean
}

export const QueryBuilder = <T,>({
  data,
  filters,
  searchText,
  confirmedSearchText,
  onSearchTextChange,
  onConfirmedSearchTextChange,
  onFiltersChange,
  isApplicantAuthorityTransfer = false,
  isIncrementalTextSearch = true,
}: Props<T>) => {
  const isNoFiltersSelected = useMemo(() => filters.length === 0, [filters])
  const selectFilter = useCallback(
    (value: T) => {
      const isSelected = filters.includes(value)
      const next = [...filters]
      if (isSelected) {
        onFiltersChange(next.filter((item) => item !== value))
      } else {
        next.push(value)
        onFiltersChange(next)
      }
      onConfirmedSearchTextChange?.(searchText ?? "")
    },
    [filters, onFiltersChange, searchText]
  )
  const selectOrUnselectAll = useCallback(() => {
    if (!isNoFiltersSelected) {
      onFiltersChange([])
      onConfirmedSearchTextChange?.(searchText ?? "")
      return
    }
  }, [isNoFiltersSelected, onFiltersChange, searchText])
  const isSearchTextConfirmed = useMemo(() => searchText === confirmedSearchText && searchText !== "", [searchText, confirmedSearchText])
  const isConfirmButtonDisabled = useMemo(() => searchText === "" && confirmedSearchText === "", [searchText, confirmedSearchText])
  const onConfirmButton = useCallback(() => {
    onConfirmedSearchTextChange?.(searchText ?? "")
  }, [onConfirmedSearchTextChange, searchText])
  const onClearButton = useCallback(() => {
    onSearchTextChange?.("")
    onConfirmedSearchTextChange?.("")
  }, [onSearchTextChange, onConfirmedSearchTextChange])
  const onSearchInputTextChange = useCallback((text: string) => {
    onSearchTextChange?.(text)
  }, [onSearchTextChange])
  const onSubmitEditing = useCallback(() => {
    if (isIncrementalTextSearch) return
    // 確定している場合は何もしない(クリアされてしまうので)
    if (!isSearchTextConfirmed) {
      onConfirmButton()
    }
  }, [onConfirmButton, isSearchTextConfirmed, isIncrementalTextSearch])

  return (
    <View>
      <View style={styles.separator}/>
      <View style={styles.searchContainer}>
        <View style={styles.searchInputWrapper}>
          <SearchInput
            value={searchText}
            onChangeText={onSearchInputTextChange}
            onSubmitEditing={onSubmitEditing}
            returnKeyType={isIncrementalTextSearch ? "default" : "search"}
            style={{ borderTopWidth: 0, borderBottomWidth: 0 }}
          />
        </View>
        {!isIncrementalTextSearch && !isSearchTextConfirmed && (
          <Button
            style={[styles.searchButton]}
            disabled={isConfirmButtonDisabled}
            onPress={onConfirmButton}
          >
            <Text style={[styles.searchButtonText]}>
              検索
            </Text>
          </Button>
        )}
        {!isIncrementalTextSearch && isSearchTextConfirmed && (
          <Button
            style={[styles.clearButton]}
            onPress={onClearButton}
          >
            <CloseIcon/>
          </Button>
        )}
      </View>
      <View style={styles.separator}/>
      {!isApplicantAuthorityTransfer && (
        <ScrollView contentContainerStyle={styles.container} horizontal={true} showsHorizontalScrollIndicator={false}>
          <Button
            style={[commonButtonStyles.commonStyle, isNoFiltersSelected && styles.filterButtonSelected]}
            onPress={selectOrUnselectAll}
          >
            <Text style={[styles.buttonText, isNoFiltersSelected ? styles.buttonTextActive : styles.buttonTextInactive]}>
              すべて
            </Text>
          </Button>
          {data.map((item) => (
            <Button
              style={[commonButtonStyles.commonStyle, filters.includes(item.value) && styles.filterButtonSelected]}
              key={String(item.value)}
              onPress={() => selectFilter(item.value)}
            >
              <Text
                style={[styles.buttonText, filters.includes(item.value) ? styles.buttonTextActive : styles.buttonTextInactive]}
              >
                {item.label}
              </Text>
            </Button>
          ))}
        </ScrollView>
      )}
    </View>
  )
}

const commonButtonStyles = StyleSheet.create({
  commonStyle: {
    marginLeft: 8,
    height: 38,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#ddd",
    backgroundColor: Colors.white3,
  },
})

const styles = StyleSheet.create({
  container: {
    paddingVertical: 8,
    paddingRight: 8,
  },
  buttonText: {
    fontSize: 13,
  },
  buttonTextInactive: {
    color: Colors.coolGrey,
  },
  buttonTextActive: {
    color: Colors.greyshBrown,
  },
  filterButtonSelected: {
    borderColor: Colors.orange,
    backgroundColor: Colors.pale,
  },
  searchButtonContainer: {
    backgroundColor: Colors.orange,
    borderColor: Colors.orange,
  },
  searchInputWrapper: {
    flex: 1,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.white3
  },
  searchButton: {
    marginRight: 16,
    height: 38,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.white
  },
  searchButtonText: {
    fontSize: 13,
    color: Colors.orange
  },
  clearButton: {
    marginRight: 8,
    padding: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  separator: {
    height: 1,
    backgroundColor: Colors.white2
  }
})
